/**
 * レシピトップページ
 * 最近チェックしたレシピ - localStorage + Splide + template 方式
 * 人気のユーザースライダー - プログレスバー付きスライダー
 * おすすめ商品スライダー - 商品スライダー
 */

import Splide from "@splidejs/splide";
import { breakpoint } from "../constant/breakpoint";
import { initProductSlider, initProgressSlider } from "../util/sliderHelpers";
import { getViewedRecipeEntries } from "./articleRecipe";
import type { ViewedRecipeEntry } from "./articleRecipe";

/**
 * template 要素からレシピカードを生成しデータをセット
 */
const createRecipeCard = (
  template: HTMLTemplateElement,
  entry: ViewedRecipeEntry,
): HTMLElement | null => {
  const clone = template.content.firstElementChild?.cloneNode(
    true,
  ) as HTMLElement;
  if (!clone) return null;

  // 画像
  const img = clone.querySelector("[data-recipe-image]") as HTMLImageElement;
  if (img && entry.image) {
    img.src = entry.image;
    img.alt = entry.title;
  }

  // タイトル・リンク
  const link = clone.querySelector("[data-recipe-link]") as HTMLAnchorElement;
  if (link) link.href = entry.url;
  const title = clone.querySelector("[data-recipe-title]") as HTMLElement;
  if (title) title.textContent = entry.title;

  // 著者リンク
  const authorLink = clone.querySelector(
    "[data-recipe-author-link]",
  ) as HTMLAnchorElement;
  if (authorLink && (entry.authorImg || entry.authorName)) {
    if (entry.authorPageUrl) {
      authorLink.href = entry.authorPageUrl;
    } else {
      authorLink.removeAttribute("href");
    }
    authorLink.style.display = "";
  }

  // 著者画像
  const authorImgWrapper = clone.querySelector(
    "[data-recipe-author-img-wrapper]",
  ) as HTMLElement;
  const authorImg = clone.querySelector(
    "[data-recipe-author-img]",
  ) as HTMLImageElement;
  if (authorImg && entry.authorImg) {
    authorImg.src = entry.authorImg;
    authorImg.alt = entry.authorName || "";
    if (authorImgWrapper) authorImgWrapper.style.display = "";
  }

  // 著者名
  const authorName = clone.querySelector(
    "[data-recipe-author-name]",
  ) as HTMLElement;
  if (authorName && entry.authorName) {
    authorName.textContent = entry.authorName;
  }

  return clone;
};

/**
 * 最近チェックしたレシピセクションを初期化
 */
const initRecentRecipesSection = (): void => {
  const section = document.querySelector<HTMLElement>(
    "[data-recently-viewed-recipe-section]",
  );
  if (!section) return;

  const list = section.querySelector<HTMLElement>(
    "[data-recently-viewed-recipe-list]",
  );
  if (!list) return;

  const sectionId =
    section.dataset.sectionId ||
    section.closest("[id]")?.id?.replace("shopify-section-", "") ||
    "";
  const template = document.getElementById(
    `recently-viewed-recipe-template-${sectionId}`,
  ) as HTMLTemplateElement;
  if (!template) return;

  // 現在の記事詳細ページの場合、そのハンドルを除外
  const win = window as unknown as Record<string, unknown>;
  const currentArticleHandle = win.__ARTICLE_HANDLE__ as string | undefined;

  const entries = getViewedRecipeEntries(currentArticleHandle);

  if (entries.length === 0) {
    section.style.display = "none";
    return;
  }

  // template からカードを生成してリストに追加
  for (const entry of entries) {
    const card = createRecipeCard(template, entry);
    if (card) list.appendChild(card);
  }

  // セクションを表示
  section.style.display = "";

  // Splide スライダーを初期化
  const sliderElement = section.querySelector<HTMLElement>(
    "[data-recently-viewed-recipe-slider]",
  );
  if (sliderElement) {
    new Splide(sliderElement, {
      type: "slide",
      perPage: 5,
      perMove: 1,
      gap: "16px",
      pagination: false,
      arrows: true,
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          perPage: 2,
          gap: "8px",
          padding: { left: "16px", right: "16px" },
          fixedWidth: "40%",
        },
      },
    }).mount();
  }
};

/**
 * ======================================
 * Popular Users - 人気のユーザースライダー
 * ======================================
 */

/**
 * Popular Usersスライダーを初期化
 */
const initPopularUsersSlider = (): void => {
  initProgressSlider(
    "[data-progress-slider='popular-users']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "16px",
      pagination: true,
      arrows: true,
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      fixedWidth: "358px",
      focus: "center",
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          perPage: 1,
          perMove: 1,
          fixedWidth: "100%",
          focus: "center",
        },
      },
    },
  );
};

/**
 * ======================================
 * Recipe Top Products - おすすめ商品スライダー
 * ======================================
 */

/**
 * レシピトップ商品スライダーを初期化
 */
const initRecipeTopProductsSlider = (): void => {
  initProductSlider("[data-product-slider='recipe-products']");
};

export const recipeTop = (): void => {
  // 最近チェックしたレシピセクションを初期化
  initRecentRecipesSection();
  // 人気のユーザースライダーを初期化（プログレスバー付き）
  initPopularUsersSlider();
  // おすすめ商品スライダーを初期化
  initRecipeTopProductsSlider();
};
